import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { UnifiedModal } from '@/components/common/UnifiedModal';
import { PAYMENT_TYPOGRAPHY, PAYMENT_SPACING } from '@/constants/residents/mobile/paymentDesignTokens';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { formatCurrency } from '@/utils/common/formatting';
import { mobilePaymentsStrings } from '@/constants/residents/strings';
import { getStatusLabel, PaymentStatusIcon } from '@/features/payments/utils/paymentStatusUtils';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import type { HistoryRow } from '@/features/payments/hooks/useLeaseHistory';

const InfoCircleIcon = RESIDENTS_SVG_COMPONENTS.INFORMATION_CIRCLE;

interface TransactionDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  transaction: HistoryRow | null;
}

/**
 * Transaction Details Modal
 * Shows detailed information about a transaction/payment.
 * Uses UnifiedModal variant="sheet" (bottom sheet on mobile, dialog on desktop).
 */
export const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({
  visible,
  onClose,
  transaction,
}) => {
  const { colors } = useThemeColors();

  if (!transaction) return null;

  const formatAmountWithCents = (amount: number): { dollars: string; cents: string } => {
    const formatted = formatCurrency(Math.abs(amount));
    const parts = formatted.split('.');
    return {
      dollars: parts[0],
      cents: parts[1] ? `.${parts[1]}` : '.00',
    };
  };

  const amountParts = formatAmountWithCents(transaction.amount);
  const isPending = transaction.status === 'pending';
  const isDeclined = transaction.status === 'declined';

  return (
    <UnifiedModal
      visible={visible}
      onClose={onClose}
      variant="sheet"
      title={mobilePaymentsStrings.transactionDetails}
      scrollable
    >
      {/* Amount Card */}
      <View style={[styles.amountCard, { backgroundColor: colors.card.bg }]}>
        <Text style={[styles.amountDescription, { color: colors.text.primary }]}>
          {transaction.description}
        </Text>
        <View style={styles.amountRow}>
          <Text style={[styles.amountDollars, { color: colors.text.primary }]}>
            {amountParts.dollars}
          </Text>
          <Text style={[styles.amountCents, { color: colors.text.primary }]}>
            {amountParts.cents}
          </Text>
        </View>
      </View>

      {/* Details List */}
      <View style={styles.detailsList}>
        {transaction.status && (
          <View style={styles.detailItem}>
            <Text style={[styles.detailLabel, { color: colors.text.muted }]}>
              {mobilePaymentsStrings.status}
            </Text>
            <View style={styles.statusRow}>
              <PaymentStatusIcon status={transaction.status} />
              <Text style={[styles.detailValue, { color: colors.text.primary }]}>
                {getStatusLabel(transaction.status)}
              </Text>
            </View>
          </View>
        )}

        {transaction.paymentId && (
          <View style={styles.detailItem}>
            <Text style={[styles.detailLabel, { color: colors.text.muted }]}>
              {mobilePaymentsStrings.paymentId}
            </Text>
            <Text style={[styles.detailValue, { color: colors.text.primary }]}>
              {transaction.paymentId}
            </Text>
          </View>
        )}

        {transaction.paymentMethod && (
          <View style={styles.detailItem}>
            <Text style={[styles.detailLabel, { color: colors.text.muted }]}>
              {mobilePaymentsStrings.paymentMethod}
            </Text>
            <Text style={[styles.detailValue, { color: colors.text.primary }]}>
              {transaction.paymentMethod}
            </Text>
          </View>
        )}

        <View style={styles.detailItem}>
          <Text style={[styles.detailLabel, { color: colors.text.muted }]}>
            {mobilePaymentsStrings.paymentAmount}
          </Text>
          <Text style={[styles.detailValue, { color: colors.text.primary }]}>
            {formatCurrency(Math.abs(transaction.amount))}
          </Text>
        </View>

        {transaction.convenienceFee !== undefined && transaction.convenienceFee > 0 && (
          <View style={styles.detailItem}>
            <Text style={[styles.detailLabel, { color: colors.text.muted }]}>
              {mobilePaymentsStrings.convenienceFee}
            </Text>
            <Text style={[styles.detailValue, { color: colors.text.primary }]}>
              {formatCurrency(transaction.convenienceFee)}
            </Text>
          </View>
        )}

        {transaction.totalAmount !== undefined && (
          <View style={styles.detailItem}>
            <Text style={[styles.detailLabel, { color: colors.text.muted }]}>
              {mobilePaymentsStrings.totalCharges}
            </Text>
            <Text style={[styles.detailValue, { color: colors.text.primary }]}>
              {formatCurrency(transaction.totalAmount)}
            </Text>
          </View>
        )}
      </View>

      {/* Info Message for Pending or Declined */}
      {(isPending || isDeclined) && (
        <>
          <View style={[styles.separator, { backgroundColor: colors.separator }]} />
          <View style={styles.infoMessage}>
            {InfoCircleIcon && (
              <View style={styles.infoIcon}>
                <InfoCircleIcon width={48} height={48} color={colors.text.muted} />
              </View>
            )}
            {isPending ? (
              <Text style={[styles.infoText, { color: colors.text.primary }]}>
                {mobilePaymentsStrings.paymentsPendingMessage}
              </Text>
            ) : (
              <Text style={[styles.infoText, { color: colors.text.primary }]}>
                {mobilePaymentsStrings.paymentsDeclinedMessage}{' '}
                <Text style={{ color: colors.accent.red }}>
                  {mobilePaymentsStrings.contactUs}
                </Text>
              </Text>
            )}
          </View>
        </>
      )}
    </UnifiedModal>
  );
};

const styles = StyleSheet.create({
  amountCard: {
    borderRadius: 8,
    padding: PAYMENT_SPACING.xxl,
    alignItems: 'center',
    marginTop: PAYMENT_SPACING.xxl,
    gap: PAYMENT_SPACING.xs,
  },
  amountDescription: {
    ...PAYMENT_TYPOGRAPHY.bodySmMedium,
  },
  amountRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  amountDollars: {
    fontSize: 24,
    fontWeight: '700',
    lineHeight: 36,
  },
  amountCents: {
    ...PAYMENT_TYPOGRAPHY.bodySmSemiBold,
    marginBottom: 2,
  },
  detailsList: {
    marginTop: PAYMENT_SPACING.xxl,
    gap: PAYMENT_SPACING.lg,
  },
  detailItem: {
    gap: 2,
  },
  detailLabel: {
    ...PAYMENT_TYPOGRAPHY.textXsMedium,
  },
  detailValue: {
    ...PAYMENT_TYPOGRAPHY.bodySm,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  separator: {
    height: 1,
    marginTop: PAYMENT_SPACING.xxl,
  },
  infoMessage: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: PAYMENT_SPACING.lg,
    paddingTop: PAYMENT_SPACING.xxl,
    paddingBottom: PAYMENT_SPACING.xl,
  },
  infoIcon: {
    // Icon styling
  },
  infoText: {
    flex: 1,
    ...PAYMENT_TYPOGRAPHY.bodySm,
    lineHeight: 21,
  },
});
